import { useState } from 'react'
import type { VehicleDTO, VehicleResponse } from '@wf/shared'
import { ApiError } from '../api/client'
import { newUuid } from '../offline/uuid'
import { useCreateVehicle } from './useCustomers'

const label = (v: VehicleDTO): string => `${v.plate}${v.model ? ` · ${v.model}` : ''}`
const plateKey = (s: string): string => s.replace(/\s+/g, '').toLowerCase()

/**
 * Fahrzeugauswahl im Neuen Auftrag — ersetzt das Dropdown durch ein freies Feld mit Vorschlägen aus den dem
 * Kunden zugeordneten Fahrzeugen (Fokus ohne Eingabe = alle, Tippen filtert nach Kennzeichen/Modell).
 * Ist das getippte Kennzeichen dem Kunden noch nicht zugeordnet, öffnet „Fahrzeug anlegen" ein Kurzformular
 * (Kennzeichen vorausgefüllt + Modell + FIN); nach dem Anlegen (idempotent über client_uuid) wird das neue
 * Fahrzeug automatisch übernommen. Die Auswahl wird über onChange nach oben gemeldet.
 */
export function VehiclePicker({
  customerId,
  vehicles,
  onChange,
}: {
  customerId: number
  vehicles: VehicleDTO[]
  onChange: (vehicleId: number | null) => void
}) {
  const createVehicle = useCreateVehicle(customerId)
  const [selected, setSelected] = useState<VehicleDTO | null>(null)
  const [query, setQuery] = useState('')
  const [focused, setFocused] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ plate: '', model: '', vin: '' })

  const q = query.trim().toLowerCase()
  // Im Auswahl-Modus (kein Fahrzeug übernommen) Vorschläge zeigen: bei Fokus ohne Eingabe alle, sonst gefiltert.
  const matches =
    selected == null && (focused || q)
      ? vehicles.filter((v) => !q || v.plate.toLowerCase().includes(q) || (v.model ?? '').toLowerCase().includes(q)).slice(0, 8)
      : []
  // „Fahrzeug anlegen" anbieten, wenn etwas getippt wurde und kein Kunden-Fahrzeug dieses Kennzeichen trägt.
  const canCreate = selected == null && !showForm && q !== '' && !vehicles.some((v) => plateKey(v.plate) === plateKey(query))

  function pick(v: VehicleDTO): void {
    setSelected(v)
    setQuery(label(v))
    setShowForm(false)
    setFocused(false)
    onChange(v.id)
  }
  function clear(): void {
    setSelected(null)
    setQuery('')
    setShowForm(false)
    onChange(null)
  }
  function openForm(): void {
    setForm({ plate: query.trim().toUpperCase(), model: '', vin: '' })
    setShowForm(true)
  }
  function createNew(): void {
    if (!form.plate.trim()) return
    createVehicle.mutate(
      { customerId, plate: form.plate.trim(), model: form.model.trim() || null, vin: form.vin.trim() || null, client_uuid: newUuid() },
      { onSuccess: (res) => pick((res as VehicleResponse).vehicle) },
    )
  }

  const error = createVehicle.error instanceof ApiError ? createVehicle.error.message : null

  return (
    <div className="wf-field">
      <div className="wf-labelrow">
        <span className="wf-label">Fahrzeug</span>
        {selected != null ? (
          <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={clear}>
            ✕ Anderes Fahrzeug
          </button>
        ) : null}
      </div>
      <input
        className="wf-input"
        aria-label="Fahrzeug suchen"
        autoComplete="off"
        autoCapitalize="characters"
        placeholder="Kennzeichen suchen oder eingeben …"
        value={query}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 120)}
        onChange={(e) => {
          setQuery(e.target.value)
          if (selected) {
            setSelected(null)
            onChange(null)
          }
        }}
      />

      {matches.length > 0 ? (
        <div className="wf-linelist">
          {matches.map((v) => (
            <div key={v.id} className="wf-line">
              <span className="wf-line__main">{label(v)}</span>
              <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={() => pick(v)}>
                Übernehmen
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {canCreate ? (
        <button type="button" className="wf-btn wf-btn--ghost" onClick={openForm}>
          ＋ Fahrzeug anlegen: {query.trim().toUpperCase()}
        </button>
      ) : null}

      {showForm ? (
        <div className="wf-vehicleform">
          <div className="two">
            <label className="wf-field">
              <span className="wf-label">Kennzeichen</span>
              <input
                className="wf-input"
                aria-label="Kennzeichen"
                autoCapitalize="characters"
                value={form.plate}
                onChange={(e) => setForm((f) => ({ ...f, plate: e.target.value.toUpperCase() }))}
                placeholder="B-AB 123"
              />
            </label>
            <label className="wf-field">
              <span className="wf-label">Fahrzeug</span>
              <input className="wf-input" aria-label="Modell" value={form.model} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))} placeholder="VW Golf" />
            </label>
          </div>
          <label className="wf-field">
            <span className="wf-label">FIN / VIN</span>
            <input className="wf-input" aria-label="FIN" value={form.vin} onChange={(e) => setForm((f) => ({ ...f, vin: e.target.value }))} />
          </label>
          {error ? (
            <p className="error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="wf-listfoot">
            <button type="button" className="wf-btn wf-btn--ghost" onClick={() => setShowForm(false)}>
              Abbrechen
            </button>
            <button type="button" className="wf-btn" onClick={createNew} disabled={!form.plate.trim() || createVehicle.isPending}>
              {createVehicle.isPending ? 'Anlegen…' : 'Fahrzeug anlegen'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
